import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { Star, Loader2, User } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StarRating = ({ rating, onRatingChange, readOnly = false, size = 'md' }) => {
  const [hoverRating, setHoverRating] = useState(0);
  
  const sizeClasses = {
    sm: 'h-4 w-4',
    md: 'h-6 w-6',
    lg: 'h-8 w-8'
  };
  
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          disabled={readOnly}
          className={`${readOnly ? 'cursor-default' : 'cursor-pointer'} transition-colors`}
          onMouseEnter={() => !readOnly && setHoverRating(star)}
          onMouseLeave={() => !readOnly && setHoverRating(0)}
          onClick={() => !readOnly && onRatingChange && onRatingChange(star)}
        >
          <Star
            className={`${sizeClasses[size]} ${
              star <= (hoverRating || rating)
                ? 'fill-yellow-400 text-yellow-400'
                : 'text-slate-300'
            }`}
          />
        </button>
      ))}
    </div>
  );
};

const ReviewItem = ({ review, t }) => {
  const formatDate = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleDateString();
  };

  return (
    <div className="border-b border-slate-100 py-4 last:border-b-0">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center">
            <User className="h-5 w-5 text-slate-500" />
          </div>
          <div>
            <p className="font-medium text-slate-900">{review.reviewer_name}</p>
            <p className="text-xs text-slate-500">
              {review.reviewer_type === 'professional' ? t('auth_as_pro') : t('auth_as_user')} • {formatDate(review.created_at)}
            </p>
          </div>
        </div>
        <StarRating rating={review.rating} readOnly size="sm" />
      </div>
      <p className="text-slate-700 mt-3 ml-13">{review.comment}</p>
    </div>
  );
};

const ReviewsSection = ({ professionalId, averageRating = 0, reviewCount = 0 }) => {
  const { t } = useLanguage();
  const { user, hasPaid } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [newReview, setNewReview] = useState({ rating: 5, comment: '' });

  useEffect(() => {
    fetchReviews();
  }, [professionalId]);

  const fetchReviews = async () => {
    try {
      const response = await axios.get(`${API}/reviews/${professionalId}`);
      setReviews(response.data);
    } catch (error) {
      console.error('Error fetching reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    if (!newReview.comment.trim()) {
      toast.error('Please write a comment');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/reviews`, {
        professional_id: professionalId,
        rating: newReview.rating,
        comment: newReview.comment.trim()
      });
      toast.success(t('common_success'));
      setNewReview({ rating: 5, comment: '' });
      setShowForm(false);
      fetchReviews();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common_error'));
    } finally {
      setSubmitting(false);
    }
  };

  const canReview = hasPaid && user?.professional_id !== professionalId;
  const hasReviewed = reviews.some(r => r.reviewer_id === user?.id);

  return (
    <Card className="border border-slate-200 rounded-md mt-6">
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <CardTitle 
            className="text-xl font-semibold"
            style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
          >
            {t('review_title')}
          </CardTitle>
          <div className="flex items-center gap-2">
            <StarRating rating={Math.round(averageRating)} readOnly size="sm" />
            <span className="text-slate-600 font-medium">
              {averageRating.toFixed(1)} ({reviewCount})
            </span>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Write Review Button */}
        {canReview && !hasReviewed && !showForm && (
          <Button
            onClick={() => setShowForm(true)}
            className="mb-4 bg-orange-500 hover:bg-orange-600 text-white rounded-sm"
            data-testid="write-review-btn"
          >
            <Star className="h-4 w-4 mr-2" />
            {t('review_write')}
          </Button>
        )}

        {/* Review Form */}
        {showForm && (
          <form onSubmit={handleSubmitReview} className="mb-6 p-4 bg-slate-50 rounded-md">
            <div className="space-y-4">
              <div>
                <Label className="mb-2 block">{t('review_rating')}</Label>
                <StarRating
                  rating={newReview.rating}
                  onRatingChange={(rating) => setNewReview(prev => ({ ...prev, rating }))}
                  size="lg"
                />
              </div>
              <div>
                <Label htmlFor="comment" className="mb-2 block">{t('review_comment')}</Label>
                <Textarea
                  id="comment"
                  value={newReview.comment}
                  onChange={(e) => setNewReview(prev => ({ ...prev, comment: e.target.value }))}
                  rows={4}
                  className="rounded-sm"
                  placeholder="Share your experience..."
                  data-testid="review-comment"
                />
              </div>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowForm(false)}
                  className="rounded-sm"
                >
                  {t('common_cancel')}
                </Button>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="bg-slate-900 hover:bg-slate-800 text-white rounded-sm"
                  data-testid="submit-review"
                >
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  {t('review_submit')}
                </Button>
              </div>
            </div>
          </form>
        )}

        {/* Reviews List */}
        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
          </div>
        ) : reviews.length === 0 ? (
          <p className="text-center text-slate-500 py-8">{t('review_no_reviews')}</p>
        ) : (
          <div className="divide-y divide-slate-100">
            {reviews.map((review) => (
              <ReviewItem key={review.id} review={review} t={t} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export { ReviewsSection, StarRating };
export default ReviewsSection;
